import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class PrCorpTemplateEditorService {

  constructor(private apiService: ApiService) { }

  /**
   * Get all PR Corp templates
   * @returns Observable with list of templates
   */
  getAllTemplates(): Observable<any> {
    return this.apiService.send('Get', '/api/pr-corp-templates').pipe(
      map(data => data.response.objects)
    );
  }

  /**
   * Get a specific PR Corp template by ID
   * @param templateId - The ID of the template
   * @returns Observable with template data
   */
  getTemplateById(templateId: number): Observable<any> {
    return this.apiService.send('Get', `/api/pr-corp-templates/${templateId}`).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Get a specific PR Corp template by UUID
   * @param templateUuid - The UUID of the template
   * @returns Observable with template data
   */
  getTemplateByUuid(templateUuid: string): Observable<any> {
    return this.apiService.send('Post', '/api/pr-corp-templates/all', {
      filter: {
        uuid_eq: templateUuid
      }
    }).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Create a new PR Corp template
   * @param templateData - The template data to create
   * @returns Observable with created template
   */
  createTemplate(templateData: any): Observable<any> {
    return this.apiService.send('Post', '/api/pr-corp-templates', templateData).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Update an existing PR Corp template
   * @param templateId - The ID of the template to update
   * @param templateData - The updated template data
   * @returns Observable with updated template
   */
  updateTemplate(templateId: number, templateData: any): Observable<any> {
    return this.apiService.send('Patch', `/api/pr-corp-templates/${templateId}`, templateData).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Delete a PR Corp template
   * @param templateId - The ID of the template to delete
   * @returns Observable with deletion result
   */
  deleteTemplate(templateId: number): Observable<any> {
    return this.apiService.send('Delete', `/api/pr-corp-templates/${templateId}`).pipe(
      map(data => data.response)
    );
  }

  /**
   * Search/filter PR Corp templates
   * @param filters - Filter criteria for searching templates
   * @returns Observable with filtered templates
   */
  searchTemplates(filters: any): Observable<any> {
    return this.apiService.send('Post', '/api/pr-corp-templates/all', {
      filter: filters
    }).pipe(
      map(data => data.response.objects)
    );
  }

  /**
   * Duplicate an existing PR Corp template
   * @param templateId - The ID of the template to duplicate
   * @returns Observable with duplicated template
   */
  duplicateTemplate(templateId: number): Observable<any> {
    const payload = {
      templateIdToDuplicate: templateId
    };
    return this.apiService.send('Post', '/api/pr-corp-templates/duplicate', payload).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Export template data
   * @param templateId - The ID of the template to export
   * @returns Observable with export data
   */
  exportTemplate(templateId: number): Observable<any> {
    return this.apiService.send('Get', `/api/pr-corp-templates/${templateId}/export`).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Import template data
   * @param templateData - The template data to import
   * @returns Observable with imported template
   */
  importTemplate(templateData: any): Observable<any> {
    return this.apiService.send('Post', '/api/pr-corp-templates/import', templateData).pipe(
      map(data => data.response.objects[0])
    );
  }

  /**
   * Validate template data
   * @param templateData - The template data to validate
   * @returns Observable with validation result
   */
  validateTemplate(templateData: any): Observable<any> {
    return this.apiService.send('Post', '/api/pr-corp-templates/validate', templateData).pipe(
      map(data => data.response)
    );
  }

  /**
   * Get template configuration options
   * @returns Observable with configuration options
   */
  getTemplateConfig(): Observable<any> {
    return this.apiService.send('Get', '/api/pr-corp-templates/config').pipe(
      map(data => data.response.objects[0])
    );
  }
}

